// Mode-aware progress math for the Projects view. Pure — no React, node-runnable.
// Modes: 'classic' | 'star' | 'score_only' | 'checklist' (GoalsSettingsContext).
//
// Returns { total, done, donePct, doneLabel, avgScore, maxScore }:
//   classic    → done = evaluated goals, donePct = weighted achieved %
//   star       → done = manager-verified goals (star5 flow)
//   score_only → done = scored goals, avgScore = mean score /10
//   checklist  → done = employee-checked goals, no scores
export function computeProjectProgress(goals, mode = 'classic') {
  const list = goals || [];
  const total = list.length;
  const empty = { total, done: 0, donePct: 0, doneLabel: 'done', avgScore: null, maxScore: 10 };
  if (total === 0) return empty;

  if (mode === 'checklist') {
    const done = list.filter(g => g.employeeCompleted === true).length;
    return { total, done, donePct: Math.round((done / total) * 100), doneLabel: 'checked off', avgScore: null, maxScore: null };
  }

  if (mode === 'star') {
    const done = list.filter(g => g.managerVerified === true).length;
    return { total, done, donePct: Math.round((done / total) * 100), doneLabel: 'verified', avgScore: null, maxScore: 5 };
  }

  if (mode === 'score_only') {
    const scored = list.filter(g => g.score != null);
    const avgScore = scored.length
      ? Math.round((scored.reduce((a, g) => a + g.score, 0) / scored.length) * 10) / 10
      : null;
    return { total, done: scored.length, donePct: Math.round((scored.length / total) * 100), doneLabel: 'scored', avgScore, maxScore: 10 };
  }

  // classic: weighted achieved % across evaluated goals
  const evaluated = list.filter(g => g.achievedPercent != null);
  const totalWeight = evaluated.reduce((a, g) => a + (g.weightage || 0), 0);
  const donePct = totalWeight > 0
    ? Math.round(evaluated.reduce((a, g) => a + (g.achievedPercent * (g.weightage || 0)), 0) / totalWeight)
    : 0;
  return { total, done: evaluated.length, donePct, doneLabel: 'evaluated', avgScore: null, maxScore: 10 };
}

// One goal's display state under a flow mode. Pure; callers style by `kind`:
// 'done' (green), 'waiting' (amber), 'score' (plain value), 'open' (muted).
export function goalState(g, mode = 'classic') {
  if (mode === 'checklist') {
    return g.employeeCompleted === true ? { kind: 'done', label: 'Done' } : { kind: 'open', label: 'Open' };
  }
  if (mode === 'star' || g.scoringMode === 'star5') {
    if (g.managerVerified === true) return { kind: 'done', label: 'Verified' };
    if (g.employeeCompleted === true) return { kind: 'waiting', label: 'Awaiting verify' };
    return { kind: 'open', label: 'In progress' };
  }
  if (g.score != null) return { kind: 'score', label: `${g.score}/10` };
  if (g.achievedPercent != null) return { kind: 'score', label: `${g.achievedPercent}% achieved` };
  return { kind: 'open', label: 'Not scored yet' };
}

// Self-check: `node src/pages/Goals/utils/projectProgress.js`
if (typeof process !== 'undefined' && process.argv?.[1]?.endsWith('projectProgress.js')) {
  const assert = (cond, msg) => { if (!cond) throw new Error(`self-check failed: ${msg}`); };
  const goals = [
    { achievedPercent: 100, weightage: 50, score: 8, employeeCompleted: true, managerVerified: true },
    { achievedPercent: 50, weightage: 50, score: null, employeeCompleted: false, managerVerified: null },
  ];
  assert(computeProjectProgress([], 'classic').total === 0, 'empty total');
  assert(computeProjectProgress(goals, 'classic').donePct === 75, 'classic weighted pct'); // (100*50 + 50*50) / 100
  assert(computeProjectProgress(goals, 'star').done === 1, 'star verified count');
  assert(computeProjectProgress(goals, 'star').donePct === 50, 'star pct');
  assert(computeProjectProgress(goals, 'checklist').done === 1, 'checklist done');
  assert(computeProjectProgress(goals, 'score_only').avgScore === 8, 'score_only avg');
  assert(computeProjectProgress(goals, 'score_only').done === 1, 'score_only scored count');
  assert(goalState(goals[0], 'star').kind === 'done', 'star verified state');
  assert(goalState(goals[1], 'star').kind === 'open', 'star open state');
  assert(goalState({ employeeCompleted: true }, 'checklist').label === 'Done', 'checklist done state');
  assert(goalState({ score: 7 }, 'classic').label === '7/10', 'classic score state');
  assert(goalState({}, 'classic').kind === 'open', 'classic unscored state');
  console.log('projectProgress self-check OK');
}
